/* Scrolling cards (docs/15 §12). Cards pin with CSS (position: sticky); this element writes
   --stack on every card and CSS turns it into scale(1 − 0.1 × stack) around the top centre, so
   three cards settle at 0.8 / 0.9 / 1.0.
   Timing follows Helix (measured 19.09 on the demo, which uses a ViewTimeline over the whole
   section): while the section scrolls out past the top of the viewport, card n of N shrinks during
   the n-th Nth of the section's height, from 1 to 1 − 0.1 × (cards after it). A card taller than
   the space under the header gets --sc-fit, a negative top that pins it at its bottom edge, so its
   end is always readable.
   Idle when the cards are not pinned (reduced motion, or mobile with sticky off) and off-screen. */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class MwScrollCards extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.children].filter((el) => el.classList.contains('scroll-card'));
    this.heights = [];
    this.frame = 0;
    this.visible = false;

    this.onScroll = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
    };
    this.onResize = () => {
      if (this.resizeFrame) return;
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = 0;
        this.refresh();
      });
    };
    this.onBlockSelect = (event) => requestAnimationFrame(() => this.reveal(event.target));

    this.io = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.toggleScroll();
      if (this.visible) this.onScroll();
    });
    this.io.observe(this);

    // Card heights change when images, fonts or editor content load.
    this.ro = new ResizeObserver(this.onResize);
    this.cards.forEach((card) => this.ro.observe(card));

    window.addEventListener('resize', this.onResize, { passive: true });
    reducedMotion.addEventListener('change', this.onResize);
    this.addEventListener('shopify:block:select', this.onBlockSelect);

    this.pauseVideos();
    this.refresh();
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.ro?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    reducedMotion.removeEventListener('change', this.onResize);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
  }

  /** Pinned = the stylesheet made the cards sticky for this viewport and motion preference. */
  get pinned() {
    return this.cards.length > 1 && getComputedStyle(this.cards[0]).position === 'sticky';
  }

  refresh() {
    this.cards.forEach((card) => card.style.removeProperty('--sc-fit'));
    this.active = this.pinned;
    if (!this.active) {
      this.cards.forEach((card) => card.style.removeProperty('--stack'));
      this.toggleScroll();
      return;
    }

    this.heights = this.cards.map((card) => card.offsetHeight);
    const viewport = window.innerHeight;
    const margin = parseFloat(getComputedStyle(this).getPropertyValue('--sc-top')) || 0;
    this.cards.forEach((card, index) => {
      const top = parseFloat(getComputedStyle(card).top) || 0;
      const overflow = top + this.heights[index] + margin - viewport;
      if (overflow > 0) card.style.setProperty('--sc-fit', `${Math.round(top - overflow)}px`);
    });

    this.toggleScroll();
    this.update();
  }

  toggleScroll() {
    const listen = this.active && this.visible;
    if (listen === this.listening) return;
    this.listening = listen;
    if (listen) window.addEventListener('scroll', this.onScroll, { passive: true });
    else window.removeEventListener('scroll', this.onScroll);
  }

  update() {
    this.frame = 0;
    if (!this.active) return;

    // Helix: exit-crossing of the section (its top at the viewport top → its bottom there).
    const host = this.closest('.section') || this;
    const rect = host.getBoundingClientRect();
    const count = this.cards.length;
    const slot = Math.max(rect.height / count, 1);
    const passed = -rect.top;

    this.cards.forEach((card, index) => {
      const progress = Math.min(Math.max((passed - index * slot) / slot, 0), 1);
      const after = count - 1 - index;
      card.style.setProperty('--stack', (after * progress).toFixed(3));
    });
  }

  /** Theme editor: scroll to where the selected card has just pinned (its flow position). */
  reveal(target) {
    const card = target.closest?.('.scroll-card');
    const index = this.cards.indexOf(card);
    if (index < 0 || !this.active) return;

    const gap = parseFloat(getComputedStyle(this).rowGap) || 0;
    let flowTop = this.getBoundingClientRect().top + window.scrollY;
    for (let i = 0; i < index; i += 1) flowTop += this.heights[i] + gap;

    const top = parseFloat(getComputedStyle(card).top) || 0;
    window.scrollTo({ top: Math.max(flowTop - top, 0), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  /** Card videos autoplay muted; honour a reduced-motion preference by holding them still. */
  pauseVideos() {
    if (!reducedMotion.matches) return;
    this.querySelectorAll('video[autoplay]').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  }
}

if (!customElements.get('mw-scroll-cards')) customElements.define('mw-scroll-cards', MwScrollCards);
